import { type Dispatch, type MouseEvent, type SetStateAction, useEffect } from 'react';
import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';
import loadable from '@loadable/component';

import {
    type AvailabilitiesCalendarSubtype,
    type CheckoutCategorySubtype,
    type ClickType,
} from '@jsmdg/tracking';
import {
    Breakpoint,
    ButtonVariant,
    NotificationVariant,
    useBreakpoint,
    useIntersection,
    useSnackbarContext,
} from '@jsmdg/yoshi';
import { getBookableLocationsCount, isExperience } from '../../../shared/product-safeguards';
import { isAddedToCartDirectly } from '../../helper/deliveryFormat';
import { useBuybox } from '../../hooks/useBuybox';
import { useBuyboxPayPalCheckout } from '../../hooks/useBuyboxPayPalCheckout';
import { useOverlayContext } from '../../hooks/useOverlayContext';
import { usePrice } from '../../hooks/usePrice';
import { useProduct } from '../../hooks/useProduct';
import { SellOption } from '../../types/enums/sellOption';
import { Buybox } from '../Buybox';
import { CartLinkSnackbarContent } from '../CartLinkSnackbarContent/CartLinkSnackbarContent';

const CookieWarningModal = loadable(async () => import('../CookieWarningModal/CookieWarningModal'));
const LoadableBuyboxCallToAction = loadable(async () => import('../Buybox/BuyboxCallToAction'));

const MixedShoppingCartErrorModal = loadable(
    async () => import('../BuyboxErrorModal/MixedShoppingCartErrorModal'),
);

const AddItemToCartErrorModal = loadable(
    async () => import('../BuyboxErrorModal/AddItemToCartErrorModal'),
);

const messages = defineMessages({
    isProductNotIntheCartErrorMessagePart1: {
        defaultMessage: 'Du hast bereits ein Ticket für ein anderes Erlebnis im Warenkorb.',
    },
    isProductNotIntheCartErrorMessagePart2: {
        defaultMessage:
            'Falls du deine Wahl ändern möchtest, musst du bitte dieses zunächst entfernen.',
    },
    a11YCloseErrorMessage: {
        defaultMessage: 'Schließen',
    },
    singleLocationTicketBooking: {
        defaultMessage: 'Termin buchen',
    },
    areaProductTicketBooking: {
        defaultMessage: 'Standort und Termin buchen',
    },
    addToCart: {
        defaultMessage: 'In den Warenkorb',
    },
    bookAnAppointment: {
        defaultMessage: 'Termin buchen',
    },
});

const getCallToActionMessage = (
    shouldAddToCart: boolean,
    bookableLocationsCount: number,
): MessageDescriptor => {
    if (shouldAddToCart) {
        return messages.addToCart;
    }

    return bookableLocationsCount > 1
        ? messages.areaProductTicketBooking
        : messages.singleLocationTicketBooking;
};

const BuyboxContainer = (): JSX.Element => {
    const intl = useIntl();
    const { openSnackbar } = useSnackbarContext();
    const { closeCartSelectionOverlay, wrapAddToCartWithOverlay } = useOverlayContext();

    const upToSMBreakpoint = useBreakpoint(Breakpoint.SM);
    const [isIntersecting, setTargetElement] = useIntersection({
        rootMargin: '0px',
        triggerOnce: false,
        root: undefined,
        threshold: 0.75,
    }) as [boolean, Dispatch<SetStateAction<HTMLElement | null>>];
    const buyboxProps = useBuybox();
    const { price } = usePrice();
    const product = useProduct();
    const bookableLocationsCount = getBookableLocationsCount(product);
    const {
        handleAddToCartClick,
        handleModalClose,
        handleRedirectOnAppointmentBooking,
        hasAddToCartError,
        hasAddingMultipleItemsError,
        hasCookieWarning,
        hasMixedShoppingCartError,
        purchaseType,
    } = buyboxProps;

    const isCheckoutDisabled = buyboxProps.isAddToCartDisabled(purchaseType);
    const isTicketOnly = isExperience(product) && product.sellOption === SellOption.TicketOnly;

    useEffect(() => {
        if (hasAddToCartError || hasMixedShoppingCartError) {
            closeCartSelectionOverlay();
        }
    }, [closeCartSelectionOverlay, hasAddToCartError, hasMixedShoppingCartError]);

    useEffect(() => {
        if (hasAddingMultipleItemsError) {
            const errorMessage = {
                title: messages.isProductNotIntheCartErrorMessagePart1,
                body: messages.isProductNotIntheCartErrorMessagePart2,
            };

            openSnackbar({
                content: <CartLinkSnackbarContent errorMessage={errorMessage} />,
                variant: NotificationVariant.Error,
                a11yTextCloseButton: intl.formatMessage(messages.a11YCloseErrorMessage),
            });
        }
    }, [hasAddingMultipleItemsError, intl, openSnackbar]);

    const callToActionText = intl.formatMessage(
        getCallToActionMessage(isAddedToCartDirectly(product), bookableLocationsCount),
    );

    // Ticket-only products cannot be bought as a voucher, so PayPal express does not apply
    const { showPayPalCheckout } = useBuyboxPayPalCheckout({
        isCheckoutDisabled: isCheckoutDisabled || isTicketOnly,
    });

    const isMobileViewport = !upToSMBreakpoint;
    const showPayPalInInline = showPayPalCheckout && (!isMobileViewport || isIntersecting);
    const showPayPalInSticky = showPayPalCheckout && isMobileViewport && !isIntersecting;

    useEffect(() => {
        const bodyClass = 'pdpBuyNowStickyActive';

        if (showPayPalCheckout && isMobileViewport) {
            document.body.classList.add(bodyClass);
        } else {
            document.body.classList.remove(bodyClass);
        }

        return () => {
            document.body.classList.remove(bodyClass);
        };
    }, [isMobileViewport, showPayPalCheckout]);

    const handleBuyboxButtonClick = (
        event?: MouseEvent<HTMLButtonElement>,
        clickType?: ClickType,
        categorySubtype?: CheckoutCategorySubtype | AvailabilitiesCalendarSubtype,
        addVoucherToCart?: boolean,
    ): void | Promise<void> => {
        if (addVoucherToCart) {
            wrapAddToCartWithOverlay(handleAddToCartClick, {
                price,
                event,
                clickType,
                categorySubtype,
            });
            return;
        }

        handleRedirectOnAppointmentBooking({
            event,
            clickType,
            clickText: intl.formatMessage(
                bookableLocationsCount > 1
                    ? messages.areaProductTicketBooking
                    : messages.singleLocationTicketBooking,
            ),
        });
    };

    return (
        <>
            {hasMixedShoppingCartError && (
                <MixedShoppingCartErrorModal
                    isOpen={hasMixedShoppingCartError}
                    onRequestClose={handleModalClose}
                />
            )}
            {hasAddToCartError && (
                <AddItemToCartErrorModal
                    isOpen={hasAddToCartError}
                    onRequestClose={handleModalClose}
                />
            )}
            {hasCookieWarning && (
                <CookieWarningModal onRequestClose={buyboxProps.handleModalClose} />
            )}
            <Buybox
                setTargetElement={setTargetElement}
                handleBuyboxButtonClick={handleBuyboxButtonClick}
                isCheckoutDisabled={isCheckoutDisabled}
                callToActionText={callToActionText}
                showPayPalButton={showPayPalInInline}
                buyboxProps={buyboxProps}
            />
            {!isCheckoutDisabled && !upToSMBreakpoint && (
                <LoadableBuyboxCallToAction
                    shouldHide={isIntersecting}
                    isCheckoutDisabled={isCheckoutDisabled}
                    handleBuyboxButtonClick={handleBuyboxButtonClick}
                    showPayPalButton={showPayPalInSticky}
                    buyboxProps={buyboxProps}
                    addVoucherToCart={!isTicketOnly}
                    variant={isTicketOnly ? ButtonVariant.Outlined : ButtonVariant.Contained}
                >
                    {isTicketOnly ? callToActionText : intl.formatMessage(messages.addToCart)}
                </LoadableBuyboxCallToAction>
            )}
        </>
    );
};

export { BuyboxContainer };
